import React, { useEffect, useMemo, useRef, useState } from 'react';
import soundManager from '../../utils/SoundManager';

/**
 * EvidenceMinigame — Whodunnit collect-clues bagging texture (immersion batch item 6).
 *
 * Six tiny pointer toys played inside the clue-inspect modal between tap and
 * reveal. Three TOOL games: DUST FOR PRINTS (rub powder off a fingerprint),
 * BLACK LIGHT (sweep a lamp until every trace glows), CHALK THE OUTLINE (drag
 * chalk around the evidence). Three AMBIENT games (Nina 08-12, teamLab feel):
 * STILL WATER (touches bloom rings of light, the evidence surfaces), INK BLOOM
 * (luminous ink spreads under the stroke and stays), FIREFLIES (a mote swarm
 * chases the pointer; lead it to the evidence and they settle).
 * TEXTURE ONLY, never a skill gate on the evidence economy: the bag
 * API call already fired at tap, every game completes with a few seconds of
 * scribbling, "skip" is always live, and a backstop timer resolves it
 * unattended. Kind is a stable hash of the clue id, so a given clue always
 * plays the same game and different clues in a scene vary.
 *
 * Pointer-event driven (mouse + touch share one path); the surface sets
 * touchAction:none so phone rubbing never scrolls the page behind the modal.
 */

const MANILA = '#d9ba72';
const INK = '#e8ddc8';
const INK_DIM = 'rgba(232,221,200,0.6)';
const CHALK = '#f5f0e0';
const UV_GLOW = '#c4b5fd';

const TEAL = 'rgba(94,234,212,0.9)';

const KINDS = ['dust', 'uv', 'trace', 'ripple', 'ink', 'fireflies'];
const kindForClue = (id) => {
    let h = 0;
    for (const ch of String(id || '')) h = ((h * 31) + ch.charCodeAt(0)) >>> 0;
    return KINDS[h % 3];
};

// Stable per-clue jitter in [-span/2, span/2] so mark layouts differ between
// clues but never move across re-renders (no Math.random in render paths).
const jitter = (id, i, span) => {
    let h = 0;
    for (const ch of `${id}:${i}`) h = ((h * 33) + ch.charCodeAt(0)) >>> 0;
    return ((h % 1000) / 1000) * span - span / 2;
};

const COPY = {
    dust: { title: 'DUST FOR PRINTS', line: 'Rub the powder away.' },
    uv: { title: 'BLACK LIGHT', line: 'Sweep until every trace glows.' },
    trace: { title: 'CHALK THE OUTLINE', line: 'Trace around the evidence.' },
    ripple: { title: 'STILL WATER', line: 'Stir it up.' },
    ink: { title: 'INK BLOOM', line: 'Spread the light.' },
    fireflies: { title: 'FIREFLIES', line: 'Lead them to the evidence.' },
};

// 🔊 Diegetic touch sound as a surface's progress count ticks up (immersion
// batch item 7). Mount and skip stay silent (counts don't move); the mute
// chip is honored inside soundManager.play. Throttle for continuous textures.
function useProgressSound(count, sound, throttleMs = 0) {
    const prevRef = useRef(count);
    const lastRef = useRef(0);
    useEffect(() => {
        if (count > prevRef.current) {
            const t = Date.now();
            if (!throttleMs || t - lastRef.current >= throttleMs) {
                lastRef.current = t;
                soundManager.play(sound);
            }
        }
        prevRef.current = count;
    }, [count, sound, throttleMs]);
}

const surfaceBase = {
    position: 'relative', height: 150, borderRadius: 10, overflow: 'hidden',
    border: `1px solid ${MANILA}33`, cursor: 'crosshair',
    touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none',
};

const ptInSurface = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top, w: r.width, h: r.height };
};

/* ── DUST: powder blobs clear under a pressed rub; a fingerprint glows beneath ── */
const DUST_COLS = 7, DUST_ROWS = 4;
function DustSurface({ finished, onCleared }) {
    const [cleared, setCleared] = useState(() => new Array(DUST_COLS * DUST_ROWS).fill(false));
    const downRef = useRef(false);
    const clearedCount = cleared.filter(Boolean).length;
    const complete = clearedCount >= Math.ceil(cleared.length * 0.7);
    useProgressSound(clearedCount, 'brushRub', 160);

    useEffect(() => {
        if (complete) onCleared();
    }, [complete, onCleared]);

    const cellCenter = (i, w, h) => ({
        x: (8 + (i % DUST_COLS) * (84 / (DUST_COLS - 1))) / 100 * w,
        y: (16 + Math.floor(i / DUST_COLS) * (68 / (DUST_ROWS - 1))) / 100 * h,
    });
    const rub = (e) => {
        const p = ptInSurface(e);
        setCleared(prev => {
            let changed = false;
            const next = prev.slice();
            for (let i = 0; i < next.length; i++) {
                if (next[i]) continue;
                const c = cellCenter(i, p.w, p.h);
                const dx = c.x - p.x, dy = c.y - p.y;
                if (dx * dx + dy * dy < 34 * 34) { next[i] = true; changed = true; }
            }
            return changed ? next : prev;
        });
    };

    return (
        <div
            style={{ ...surfaceBase, background: 'linear-gradient(160deg, #17130c, #0d0b08)' }}
            onPointerDown={(e) => { downRef.current = true; e.currentTarget.setPointerCapture(e.pointerId); rub(e); }}
            onPointerMove={(e) => { if (downRef.current) rub(e); }}
            onPointerUp={() => { downRef.current = false; }}
            onPointerCancel={() => { downRef.current = false; }}
        >
            {/* The print under the powder — arcs, not a photo: prints are the point */}
            <svg viewBox="0 0 100 100" style={{
                position: 'absolute', left: '50%', top: '50%', width: 96, height: 96,
                transform: 'translate(-50%, -50%) rotate(-8deg)',
                filter: (finished || complete) ? `drop-shadow(0 0 7px ${MANILA})` : 'none',
                transition: 'filter 0.4s',
            }}>
                {[38, 30, 22, 14, 7].map((r, i) => (
                    <ellipse key={r} cx="50" cy="50" rx={r} ry={r * 1.22}
                        fill="none" stroke={INK} strokeWidth="2.6"
                        strokeDasharray={i % 2 ? '26 7' : '34 5'} strokeLinecap="round" opacity="0.85" />
                ))}
            </svg>
            {cleared.map((isClear, i) => {
                const left = 8 + (i % DUST_COLS) * (84 / (DUST_COLS - 1));
                const top = 16 + Math.floor(i / DUST_COLS) * (68 / (DUST_ROWS - 1));
                return (
                    <div key={i} style={{
                        position: 'absolute', left: `${left}%`, top: `${top}%`,
                        width: 62, height: 46, transform: 'translate(-50%, -50%)',
                        borderRadius: '50%', pointerEvents: 'none',
                        background: 'radial-gradient(ellipse, rgba(217,186,114,0.55) 0%, rgba(217,186,114,0.28) 55%, transparent 75%)',
                        filter: 'blur(3px)',
                        opacity: (isClear || finished) ? 0 : 1, transition: 'opacity 0.35s',
                    }} />
                );
            })}
        </div>
    );
}

/* ── UV: a violet lamp follows the pointer; traces glow for keeps when swept ── */
const UV_MARKS = [{ x: 24, y: 36 }, { x: 56, y: 66 }, { x: 79, y: 30 }];
function UvSurface({ clueId, finished, onCleared }) {
    const [lit, setLit] = useState(() => new Array(UV_MARKS.length).fill(false));
    const [lamp, setLamp] = useState(null); // {x,y} px, null until first sweep
    const marks = useMemo(() => UV_MARKS.map((m, i) => ({
        x: Math.min(90, Math.max(10, m.x + jitter(clueId, i * 2, 14))),
        y: Math.min(82, Math.max(18, m.y + jitter(clueId, i * 2 + 1, 18))),
    })), [clueId]);
    const litCount = lit.filter(Boolean).length;
    useProgressSound(litCount, 'uvPing');

    useEffect(() => {
        if (litCount === marks.length) onCleared();
    }, [litCount, marks.length, onCleared]);

    // Hover sweeps too: the lamp is light, not touch — generosity over rigor
    const sweep = (e) => {
        const p = ptInSurface(e);
        setLamp({ x: p.x, y: p.y });
        setLit(prev => {
            let changed = false;
            const next = prev.slice();
            marks.forEach((m, i) => {
                if (next[i]) return;
                const dx = m.x / 100 * p.w - p.x, dy = m.y / 100 * p.h - p.y;
                if (dx * dx + dy * dy < 48 * 48) { next[i] = true; changed = true; }
            });
            return changed ? next : prev;
        });
    };

    return (
        <div
            style={{ ...surfaceBase, background: '#05060a' }}
            onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); sweep(e); }}
            onPointerMove={sweep}
        >
            {lamp && !finished && (
                <div style={{
                    position: 'absolute', left: lamp.x, top: lamp.y, width: 130, height: 130,
                    transform: 'translate(-50%, -50%)', borderRadius: '50%', pointerEvents: 'none',
                    background: 'radial-gradient(circle, rgba(150,120,255,0.30) 0%, rgba(150,120,255,0.10) 45%, transparent 70%)',
                }} />
            )}
            {marks.map((m, i) => {
                const isLit = lit[i] || finished;
                return (
                    <span key={i} style={{
                        position: 'absolute', left: `${m.x}%`, top: `${m.y}%`,
                        transform: 'translate(-50%, -50%)', pointerEvents: 'none',
                        fontSize: 22, color: UV_GLOW,
                        opacity: isLit ? 1 : 0.07,
                        textShadow: isLit ? `0 0 10px ${UV_GLOW}, 0 0 22px rgba(150,120,255,0.7)` : 'none',
                        animation: isLit ? 'evGlowIn 0.45s ease-out' : 'none',
                    }}>✦</span>
                );
            })}
            <span style={{
                position: 'absolute', right: 8, bottom: 6, pointerEvents: 'none',
                color: litCount === marks.length ? UV_GLOW : INK_DIM,
                fontWeight: 800, fontSize: 'clamp(10px, 2vmin, 12px)', fontVariantNumeric: 'tabular-nums',
            }}>{finished ? marks.length : litCount}/{marks.length}</span>
        </div>
    );
}

/* ── TRACE: drag chalk around the evidence; the dashed outline sets solid ── */
const TRACE_DOTS = 10;
function TraceSurface({ emoji, finished, onCleared }) {
    const [lit, setLit] = useState(() => new Array(TRACE_DOTS).fill(false));
    const downRef = useRef(false);
    const litCount = lit.filter(Boolean).length;
    const complete = litCount === TRACE_DOTS;
    useProgressSound(litCount, 'chalkTick');

    useEffect(() => {
        if (complete) onCleared();
    }, [complete, onCleared]);

    const dotAt = (i, w, h) => {
        const a = (i / TRACE_DOTS) * Math.PI * 2 - Math.PI / 2;
        return { x: w * (0.5 + 0.36 * Math.cos(a)), y: h * (0.52 + 0.34 * Math.sin(a)) };
    };
    const chalk = (e) => {
        const p = ptInSurface(e);
        setLit(prev => {
            let changed = false;
            const next = prev.slice();
            for (let i = 0; i < next.length; i++) {
                if (next[i]) continue;
                const d = dotAt(i, p.w, p.h);
                const dx = d.x - p.x, dy = d.y - p.y;
                if (dx * dx + dy * dy < 30 * 30) { next[i] = true; changed = true; }
            }
            return changed ? next : prev;
        });
    };
    const outlineDone = finished || complete;

    return (
        <div
            style={{ ...surfaceBase, background: 'linear-gradient(150deg, #101014, #0a0a0e)' }}
            onPointerDown={(e) => { downRef.current = true; e.currentTarget.setPointerCapture(e.pointerId); chalk(e); }}
            onPointerMove={(e) => { if (downRef.current) chalk(e); }}
            onPointerUp={() => { downRef.current = false; }}
            onPointerCancel={() => { downRef.current = false; }}
        >
            <span style={{
                position: 'absolute', left: '50%', top: '52%', transform: 'translate(-50%, -50%)',
                fontSize: 34, opacity: 0.5, filter: 'grayscale(0.8)', pointerEvents: 'none',
            }}>{emoji || '🔍'}</span>
            <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                <ellipse cx="50%" cy="52%" rx="36%" ry="34%" fill="none"
                    stroke={outlineDone ? CHALK : 'rgba(245,240,224,0.28)'}
                    strokeWidth={outlineDone ? 3 : 2}
                    strokeDasharray={outlineDone ? 'none' : '7 8'}
                    style={{ filter: outlineDone ? `drop-shadow(0 0 6px ${CHALK})` : 'none', transition: 'all 0.4s' }} />
            </svg>
            {lit.map((isLit, i) => {
                const a = (i / TRACE_DOTS) * Math.PI * 2 - Math.PI / 2;
                return (
                    <span key={i} style={{
                        position: 'absolute',
                        left: `${50 + 36 * Math.cos(a)}%`, top: `${52 + 34 * Math.sin(a)}%`,
                        transform: 'translate(-50%, -50%)', pointerEvents: 'none',
                        width: 9, height: 9, borderRadius: '50%',
                        background: (isLit || finished) ? CHALK : 'rgba(245,240,224,0.30)',
                        boxShadow: (isLit || finished) ? `0 0 8px ${CHALK}` : 'none',
                        transition: 'all 0.25s',
                    }} />
                );
            })}
        </div>
    );
}

/* ── The three AMBIENT games (Nina 08-12, teamLab feel): luminous, generative,
      alive-to-the-touch. Same no-fail contract as the tool games above. ── */

/* ── RIPPLE: dark water; every touch blooms slow rings of light; the evidence
      brightens up from underneath until it surfaces ── */
const RIPPLE_GOAL = 6;
const RIPPLE_HUES = [MANILA, TEAL, UV_GLOW];
function RippleSurface({ emoji, finished, onCleared }) {
    const [ripples, setRipples] = useState([]); // {id, x, y, hue}
    const [spawned, setSpawned] = useState(0);
    const nextId = useRef(0);
    const lastSpawn = useRef(0);
    const complete = spawned >= RIPPLE_GOAL;
    useProgressSound(spawned, 'waterPlop');

    useEffect(() => {
        if (complete) onCleared();
    }, [complete, onCleared]);

    const stir = (e) => {
        const t = Date.now();
        if (t - lastSpawn.current < 240) return;
        lastSpawn.current = t;
        const p = ptInSurface(e);
        const id = nextId.current++;
        setRipples(prev => [...prev, { id, x: p.x, y: p.y, hue: RIPPLE_HUES[id % RIPPLE_HUES.length] }]);
        setSpawned(n => n + 1);
    };

    return (
        <div
            style={{ ...surfaceBase, background: 'radial-gradient(ellipse at 50% 38%, #0a1016, #05070c)' }}
            onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); stir(e); }}
            onPointerMove={(e) => { if (e.buttons || e.pointerType === 'touch') stir(e); }}
        >
            <span style={{
                position: 'absolute', left: '50%', top: '48%', transform: 'translate(-50%, -50%)',
                fontSize: 36, pointerEvents: 'none', transition: 'opacity 0.5s, filter 0.5s',
                opacity: finished ? 1 : 0.12 + 0.8 * Math.min(1, spawned / RIPPLE_GOAL),
                filter: (finished || complete) ? `drop-shadow(0 0 10px ${TEAL})` : 'blur(1.5px)',
            }}>{emoji || '🔍'}</span>
            {ripples.map(r => (
                <span key={r.id}
                    onAnimationEnd={() => setRipples(prev => prev.filter(x => x.id !== r.id))}
                    style={{
                        position: 'absolute', left: r.x, top: r.y, width: 14, height: 14,
                        borderRadius: '50%', border: `1.5px solid ${r.hue}`, pointerEvents: 'none',
                        animation: 'evRipple 1.7s ease-out forwards',
                    }} />
            ))}
            {(finished || complete) && (
                <span style={{
                    position: 'absolute', left: '50%', top: '48%', width: 14, height: 14,
                    borderRadius: '50%', border: `1.5px solid ${TEAL}`, pointerEvents: 'none',
                    animation: 'evRipple 1.7s ease-out forwards',
                }} />
            )}
        </div>
    );
}

/* ── INK: drag and luminous ink blooms under the stroke and STAYS; paint most
      of the dark and the surface washes with light ── */
const INK_COLS = 8, INK_ROWS = 4;
const INK_HUES = ['rgba(217,186,114,0.42)', 'rgba(94,234,212,0.34)', 'rgba(196,181,253,0.38)'];
function InkSurface({ finished, onCleared }) {
    const [blobs, setBlobs] = useState([]); // {id, x, y, hue, size}
    const [covered, setCovered] = useState(() => new Array(INK_COLS * INK_ROWS).fill(false));
    const downRef = useRef(false);
    const lastPt = useRef(null);
    const nextId = useRef(0);
    const coveredCount = covered.filter(Boolean).length;
    const complete = coveredCount >= Math.ceil(covered.length * 0.62);
    useProgressSound(coveredCount, 'inkSwish', 180);

    useEffect(() => {
        if (complete) onCleared();
    }, [complete, onCleared]);

    const paint = (e) => {
        const p = ptInSurface(e);
        const lp = lastPt.current;
        if (lp && (lp.x - p.x) ** 2 + (lp.y - p.y) ** 2 < 18 * 18) return;
        lastPt.current = { x: p.x, y: p.y };
        const id = nextId.current++;
        if (id < 80) {
            setBlobs(prev => [...prev, {
                id, x: p.x, y: p.y,
                hue: INK_HUES[id % INK_HUES.length], size: 52 + (id * 7) % 26,
            }]);
        }
        setCovered(prev => {
            let changed = false;
            const next = prev.slice();
            for (let i = 0; i < next.length; i++) {
                if (next[i]) continue;
                const cx = (7 + (i % INK_COLS) * (86 / (INK_COLS - 1))) / 100 * p.w;
                const cy = (14 + Math.floor(i / INK_COLS) * (72 / (INK_ROWS - 1))) / 100 * p.h;
                if ((cx - p.x) ** 2 + (cy - p.y) ** 2 < 34 * 34) { next[i] = true; changed = true; }
            }
            return changed ? next : prev;
        });
    };

    return (
        <div
            style={{ ...surfaceBase, background: 'linear-gradient(150deg, #0a0a10, #06060a)' }}
            onPointerDown={(e) => { downRef.current = true; e.currentTarget.setPointerCapture(e.pointerId); lastPt.current = null; paint(e); }}
            onPointerMove={(e) => { if (downRef.current) paint(e); }}
            onPointerUp={() => { downRef.current = false; }}
            onPointerCancel={() => { downRef.current = false; }}
        >
            {blobs.map(b => (
                <span key={b.id} style={{
                    position: 'absolute', left: b.x, top: b.y, width: b.size, height: b.size,
                    marginLeft: -b.size / 2, marginTop: -b.size / 2, borderRadius: '50%',
                    background: `radial-gradient(circle, ${b.hue} 0%, transparent 70%)`,
                    pointerEvents: 'none', animation: 'evBloom 0.9s ease-out forwards',
                }} />
            ))}
            <div style={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                background: `radial-gradient(ellipse at 50% 50%, ${INK_HUES[0]}, ${INK_HUES[2]} 60%, transparent 90%)`,
                opacity: (finished || complete) ? 0.55 : 0, transition: 'opacity 0.6s',
            }} />
        </div>
    );
}

/* ── FIREFLIES: glowing motes lazily chase the pointer (CSS-transition lag =
      organic trailing); lead them over the evidence and they settle one by one ── */
const FLY_N = 12;
function FireflySurface({ clueId, emoji, finished, onCleared }) {
    const [target, setTarget] = useState(null); // {x, y} px — where the swarm heads
    const [settled, setSettled] = useState(() => new Array(FLY_N).fill(false));
    const lastSettle = useRef(0);
    const nest = useMemo(() => ({
        x: 50 + jitter(clueId, 9, 34), y: 54 + jitter(clueId, 10, 22),
    }), [clueId]);
    const settledCount = settled.filter(Boolean).length;
    const complete = settledCount === FLY_N;
    useProgressSound(settledCount, 'fireflyChime');

    useEffect(() => {
        if (complete) onCleared();
    }, [complete, onCleared]);

    // Hover counts (fireflies come to an outstretched hand); touch drags too
    const lead = (e) => {
        const p = ptInSurface(e);
        setTarget({ x: p.x, y: p.y });
        const nx = nest.x / 100 * p.w, ny = nest.y / 100 * p.h;
        if ((nx - p.x) ** 2 + (ny - p.y) ** 2 < 36 * 36) {
            const t = Date.now();
            if (t - lastSettle.current < 150) return;
            lastSettle.current = t;
            setSettled(prev => {
                const i = prev.indexOf(false);
                if (i === -1) return prev;
                const next = prev.slice();
                next[i] = true;
                return next;
            });
        }
    };

    return (
        <div
            style={{ ...surfaceBase, background: 'radial-gradient(ellipse at 50% 30%, #0b0d14, #050609)' }}
            onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); lead(e); }}
            onPointerMove={lead}
        >
            <span style={{
                position: 'absolute', left: `${nest.x}%`, top: `${nest.y}%`,
                transform: 'translate(-50%, -50%)', fontSize: 30, pointerEvents: 'none',
                opacity: (finished || complete) ? 1 : 0.25, transition: 'opacity 0.5s, filter 0.5s',
                filter: (finished || complete) ? `drop-shadow(0 0 10px ${MANILA})` : 'grayscale(0.6)',
            }}>{emoji || '🔍'}</span>
            {settled.map((isSet, i) => {
                const a = (i / FLY_N) * Math.PI * 2;
                const hue = i % 2 ? TEAL : MANILA;
                // settled: ring the nest · chasing: trail the pointer in a loose
                // cloud · idle: scattered roost — CSS transitions do the flying
                const pos = (isSet || finished)
                    ? { left: `calc(${nest.x}% + ${Math.round(22 * Math.cos(a))}px)`, top: `calc(${nest.y}% + ${Math.round(16 * Math.sin(a))}px)` }
                    : target
                        ? { left: target.x + jitter(clueId, i + 20, 56), top: target.y + jitter(clueId, i + 40, 44) }
                        : { left: `${12 + (i * 83 + 11) % 76}%`, top: `${14 + (i * 61 + 7) % 64}%` };
                return (
                    <span key={i} style={{
                        position: 'absolute', ...pos, width: 5, height: 5, borderRadius: '50%',
                        background: hue, pointerEvents: 'none',
                        boxShadow: `0 0 7px 2px ${hue}`,
                        transition: `left ${(0.7 + (i % 4) * 0.25).toFixed(2)}s ease-out, top ${(0.8 + (i % 3) * 0.25).toFixed(2)}s ease-out`,
                        animation: `evFlicker ${(1.4 + (i % 5) * 0.4).toFixed(1)}s ease-in-out infinite`,
                    }} />
                );
            })}
            <span style={{
                position: 'absolute', right: 8, bottom: 6, pointerEvents: 'none',
                color: complete ? MANILA : INK_DIM, fontWeight: 800,
                fontSize: 'clamp(10px, 2vmin, 12px)', fontVariantNumeric: 'tabular-nums',
            }}>{finished ? FLY_N : settledCount}/{FLY_N}</span>
        </div>
    );
}

export default function EvidenceMinigame({ clueId, emoji, onComplete }) {
    const kind = useMemo(() => kindForClue(clueId), [clueId]);
    const [finished, setFinished] = useState(false);
    const doneRef = useRef(false);
    const mountedRef = useRef(true);
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    const finishRef = useRef(null);
    finishRef.current = () => {
        if (doneRef.current) return;
        doneRef.current = true;
        setFinished(true);
        // Beat: let the surface show its finished state before the reveal takes over
        setTimeout(() => { if (mountedRef.current && onCompleteRef.current) onCompleteRef.current(); }, 450);
    };
    const finish = () => finishRef.current();

    // ⏱ Backstop: texture, never a gate — walk away and it bags itself
    useEffect(() => {
        mountedRef.current = true;
        const t = setTimeout(() => finishRef.current(), 12000);
        return () => { mountedRef.current = false; clearTimeout(t); };
    }, []);

    return (
        <div style={{ textAlign: 'center' }}>
            <style>{`
                @keyframes evGlowIn { from { transform: translate(-50%,-50%) scale(2.1); opacity: 0; } to { transform: translate(-50%,-50%) scale(1); opacity: 1; } }
                @keyframes evRipple { from { transform: translate(-50%,-50%) scale(0.25); opacity: 0.9; } to { transform: translate(-50%,-50%) scale(13); opacity: 0; } }
                @keyframes evBloom { from { transform: scale(0.15); opacity: 0; } 40% { opacity: 1; } to { transform: scale(1); opacity: 0.85; } }
                @keyframes evFlicker { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
            `}</style>
            <div style={{ color: MANILA, fontWeight: 900, letterSpacing: '0.1em', fontSize: 'clamp(11px, 2.2vmin, 13px)' }}>
                {COPY[kind].title}
            </div>
            <div style={{ color: INK_DIM, fontSize: 'clamp(10px, 2vmin, 12px)', marginTop: 2, marginBottom: 8 }}>
                {COPY[kind].line}
            </div>
            {kind === 'dust' && <DustSurface finished={finished} onCleared={finish} />}
            {kind === 'uv' && <UvSurface clueId={clueId} finished={finished} onCleared={finish} />}
            {kind === 'trace' && <TraceSurface emoji={emoji} finished={finished} onCleared={finish} />}
            {kind === 'ripple' && <RippleSurface emoji={emoji} finished={finished} onCleared={finish} />}
            {kind === 'ink' && <InkSurface finished={finished} onCleared={finish} />}
            {kind === 'fireflies' && <FireflySurface clueId={clueId} emoji={emoji} finished={finished} onCleared={finish} />}
            <button onClick={finish} style={{
                background: 'none', border: 'none', color: INK_DIM, textDecoration: 'underline',
                cursor: 'pointer', fontSize: 'clamp(10px, 2vmin, 12px)', marginTop: 6, padding: 4,
            }}>skip</button>
        </div>
    );
}
